import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import API_BASE_URL from "../../config";
import "./OverViewN.css";

const Overview = () => {
  const { t } = useTranslation();
  const [devices, setDevices] = useState([]);
  const [pingResults, setPingResults] = useState({});
  const [loading, setLoading] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);

  const handlePing = (ip) => {
    setLoading(ip);
    fetch(`${API_BASE_URL}/overview/ping-devices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ip }),
    })
      .then((res) => res.json())
      .then((data) => {
        const status = data.status === "success"
          ? `${t('online')} (${data.response_time_ms} ms)`
          : t('offline');
        setPingResults((prev) => ({ ...prev, [ip]: status }));
      })
      .catch(() => {
        setPingResults((prev) => ({ ...prev, [ip]: t('error') }));
      })
      .finally(() => setLoading(null));
  };

  useEffect(() => {
    fetch(`${API_BASE_URL}/overview/get-devices`)
      .then((res) => res.json())
      .then((data) => {
        setDevices(data);
        const initialPingResults = {};
        data.forEach((device) => {
          initialPingResults[device.ip_address] = t('pending');
        });
        setPingResults(initialPingResults);
        data.forEach((device) => handlePing(device.ip_address));
      })
      .catch((err) => console.error(t('failed_fetch_devices'), err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const getStatusClass = (ip) => {
    if (loading === ip) return "checking";
    const status = pingResults[ip] || "";
    if (status.includes(t('online'))) return "online";
    if (status.includes(t('offline'))) return "offline";
    if (status === t('error')) return "offline";
    return "";
  };

  const getSummary = () => {
    const summary = {
      totalDevices: devices.length,
      onlineDevices: 0,
      offlineDevices: 0,
      switch: 0,
      router: 0,
      olt: 0,
    };

    devices.forEach((device) => {
      const ip = device.ip_address;
      const type = device.device_type?.toLowerCase();
      const statusClass = getStatusClass(ip);

      if (statusClass === "online") summary.onlineDevices++;
      else if (statusClass === "offline") summary.offlineDevices++;

      if (type === "switch") summary.switch++;
      else if (type === "router") summary.router++;
      else if (type === "olt") summary.olt++;
    });

    return summary;
  };

  const summary = getSummary();

  const filters = [
    { label: t('total_devices'), value: "totalDevices", count: summary.totalDevices },
    { label: t('online_devices'), value: "onlineDevices", count: summary.onlineDevices },
    { label: t('offline_devices'), value: "offlineDevices", count: summary.offlineDevices },
    { label: t('switch'), value: "switch", count: summary.switch },
    { label: t('router'), value: "router", count: summary.router },
    { label: t('olt'), value: "olt", count: summary.olt },
  ];

  const filteredDevices = devices.filter((device) => {
    if (!selectedFilter) return false;

    const ip = device.ip_address;
    const type = device.device_type?.toLowerCase();
    const status = getStatusClass(ip);

    switch (selectedFilter) {
      case "totalDevices":
        return true;
      case "onlineDevices":
        return status === "online";
      case "offlineDevices":
        return status === "offline";
      case "switch":
      case "router":
      case "olt":
        return type === selectedFilter;
      default:
        return false;
    }
  });

  return (
    <div className="overview-container">
      <h2 className="title">{t('Network status')}</h2>
      <h3 className="title">{t('NOC Wall')}</h3>

      <div className="filter-grid">
        {filters.map((filter) => (
          <div
            key={filter.value}
            onClick={() => setSelectedFilter(filter.value)}
            className={`filter-box ${filter.value} ${selectedFilter === filter.value ? "selected" : ""}`}
          >
            <div className="filter-label">{filter.label}</div>
            <div className="filter-count">{filter.count}</div>
          </div>
        ))}
      </div>

      {selectedFilter && (
        <div className="device-section">
          <h3 className="device-heading">
            {t('showing')} {filters.find((f) => f.value === selectedFilter)?.label}
          </h3>
          <div className="device-grid">
            {filteredDevices.map((device) => {
              const ip = device.ip_address;
              const type = device.device_type?.toLowerCase();
              const statusClass = getStatusClass(ip);
              const boxClass = `device-box ${type} ${statusClass}`;

              return (
                <div key={device.id} className={boxClass}>
                  <div className="dev-card-header">
                    <span className="dev-hostname">{device.hostname}</span>
                    <span className="dev-ip">{ip}</span>
                  </div>

                  <div className="dev-latency-display">
                    {loading === ip ? (
                      <span style={{ color: '#ffc107', fontSize: '16px' }}>Checking...</span>
                    ) : (
                      <span>{pingResults[ip]}</span>
                    )}
                  </div>

                  <div className="dev-card-footer">
                    <span>Type: <b>{device.device_type}</b></span>
                    <span>Area ID: <b>{device.setup_location}</b></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;